<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-8">
            <div class="col-sm-2">
              <router-link class="btn btn-primary" to="/admin-chat" tag="button">Back</router-link>
            </div>
          </div>
          <div class="col-sm-4">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i :class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4">
            <h1 class="m-0">Chat {{ this.$route.params.table_id }}<span v-if="dealer">, {{ dealer }}</span></h1>
          </div>
        </div>
      </div>
    </div>
    <section class="content">
      <b-card-group deck>
        <div class="container-fluid">
          <b-row class="col-lg-12">
            <b-col class="col-lg-4 mt-4">
              <b-card header-tag="header">
                <template #header>
                  <b>Table: {{ tableId }}, {{ tableName }}</b>
                </template>
                <b-card-text>
                  <div class="stats-info">
                    <div>Number of users: <b>{{ Object.keys(users).length }}</b></div>
                    <div>Number of messages: <b>{{ Object.keys(messages).length }}</b></div>
                  </div>

                  <div class="players-section">
                    <div class="search-box">
                      Players:
                      <input type="text" v-model="search" placeholder="User name" />
                    </div>

                    <div class="users-list">
                      <b-spinner v-if="userBlockInProcess" label="Loading..." />
                      <div v-else>
                        <div v-for="(user, index) in filteredUsers" :key="index" class="user-item">
                          <b-link :class="getUserLinkClass(user)" @click="navigateToChatHistory(user)"
                            :disabled="!user.uid" :style="!user.uid ? 'pointer-events: none; opacity: 0.6;' : ''">
                            {{ user.userName }} <span v-if="user.operatorName">({{ user.operatorName }})</span>
                          </b-link>
                          <button v-if="canBlockUser(user)" class="btn btn-sm" :class="getBlockButtonClass(user)"
                            @click="blockUser(user)">
                            {{ user.chatBlock === 0 ? "Block" : "Unblock" }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card-text>
              </b-card>
            </b-col>
            <b-col class="col-lg-8 mt-4">
              <b-card header-tag="header" footer-tag="footer">
                <template #header>
                  <b>Table: {{ tableId }}, {{ tableName }}</b>
                </template>
                <b-col ref="chatArea" :data-tableId="tableId" class="chat-area col-lg-12" @scroll="handleScroll">
                  <p v-for="(message, key) in messages" :key="key" class="message" :class="getMessageClass(message)">
                    <b>{{ message.userName }}:</b>
                    {{ message.message }}
                    <i class="timestamp">{{ message.timestamp }}</i>
                  </p>
                </b-col>
                <template #footer>
                  <b-col class="col-lg-12">
                    <form @submit.prevent="handleSubmit(tableId)">
                      <b-button-group class="col-lg-5 float-right">
                        <b-button type="submit" variant="primary" @click="setSubmitType(1)" :disabled="isSending">
                          <message-send-status :is-sending="isSending" text="To dealer" />
                        </b-button>
                        <b-button type="submit" variant="success" @click="setSubmitType(2)" :disabled="isSending">
                          <message-send-status :is-sending="isSending" text="To All" />
                        </b-button>
                      </b-button-group>
                      <b-form-input :id="`${tableId}-input`" v-model="formValues[tableId]" placeholder="message"
                        minlength="2" maxlength="250" :state="validateState(`${tableId}-input`)"
                        :aria-describedby="`${tableId}-input-feedback`" required class="col-lg-7 mt-2"
                        :class="{ 'is-invalid': isInvalidMessage[tableId] }" />
                    </form>
                  </b-col>
                </template>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </b-card-group>
    </section>
  </div>
</template>
<script>
import { getRouteIcon, ROUTE_ADMIN_CHAT } from "@/utils/routerIcons";
import FilterButtons from "@/components/FilterButtons.vue";
import Multiselect from "vue-multiselect";
import MessageSendStatus from "@/components/AdminChat/MessageSendStatus.vue";

const COLORS_MAP = {
  green: "#00ff00"
};

const HISTORY_CHAT_HOURS = 24;
const REFRESH_CHAT_MS = 10000;

export default {
  name: 'ShowChat',

  components: {
    Multiselect,
    FilterButtons,
    MessageSendStatus
  },

  data() {
    return {
      search: null,
      messages: {},
      tableName: "",
      tableId: 0,
      users: [],
      formValues: this.getInitialValues(),
      submitType: 1,
      isSending: false,
      isNeedScroll: [],
      isInvalidMessage: [],
      userBlockInProcess: false,
      refresherId: null,
      dealer: null
    };
  },

  computed: {
    routeIcon: () => getRouteIcon(ROUTE_ADMIN_CHAT),

    filteredUsers() {
      if (!this.search) return this.users;

      return Object.values(this.users).filter(user =>
        user.userName.toLowerCase().includes(this.search.toLowerCase())
      );
    }
  },

  watch: {
    filters: {
      handler() {
        this.tables = [];
        this.loadChats(HISTORY_CHAT_HOURS);
      },
      deep: true
    }
  },

  mounted() {
    this.loadChats(HISTORY_CHAT_HOURS);
    this.refreshChat();
    setTimeout(() => this.loadDealer(), 3000);
  },

  destroyed() {
    if (this.refresherId) {
      clearInterval(this.refresherId);
      this.refresherId = null;
    }
  },

  methods: {
    getInitialValues() {
      return {
        apiKey: process.env.VUE_APP_CHAT_API_KEY,
        message: null,
        color: "red",
        username: "admin",
        tableIds: null,
        type: 1
      };
    },

    async loadDealer() {
      try {
        const response = await this.$http({
          url: "/api/v1/admin-chat/get-dealer-tables",
          data: {
            apiKey: process.env.VUE_APP_CHAT_API_KEY,
            tableId: this.$route.params.table_id
          },
          method: "POST"
        });
        this.dealer = response.data;
      } catch (error) {
        console.error('Error loading dealer:', error);
      }
    },

    async loadChats(hours) {
      try {
        const dateFrom = new Date();
        dateFrom.setHours(dateFrom.getHours() - hours);

        const response = await this.$http({
          url: "/api/v1/admin-chat-by-id",
          params: {
            dateFrom,
            tableId: this.$route.params.table_id
          },
          method: "GET"
        });

        const { messages, name, tableId, users } = response.data;
        this.tableName = name;
        this.tableId = tableId;
        this.users = users;
        
        const currentMessageIds = Object.keys(this.messages);
        const newMessageIds = Object.keys(messages);
        
        const hasNewMessages = newMessageIds.some(id => !currentMessageIds.includes(id));
        
        if (hasNewMessages) {
          this.messages = messages;
          this.scrollDown();
        }
      } catch (error) {
        console.error('Error loading chats:', error);
      }
    },

    async blockUser(user) {
      try {
        const response = await this.$http({
          url: '/api/v1/chat-history/block-user',
          method: 'POST',
          data: {
            playerId: user.uid,
            partnerId: user.partnerId,
            key: process.env.VUE_APP_CHAT_API_KEY
          }
        });
        user.chatBlock = response.data.chatBlock;
      } catch (error) {
        console.error('Error blocking user:', error);
      }
    },

    refreshChat() {
      this.refresherId = setInterval(() => {
        this.loadChats(HISTORY_CHAT_HOURS);
      }, REFRESH_CHAT_MS);
    },

    async handleSubmit(tableId) {
      if (!this.isValidMessage(this.formValues[tableId])) {
        this.isInvalidMessage[tableId] = true;
        return;
      }

      this.isInvalidMessage[tableId] = false;
      this.isSending = true;

      try {
        await this.$http({
          url: "/api/v1/admin-chat",
          method: "POST",
          data: {
            ...this.formValues,
            message: this.formValues[tableId],
            type: this.submitType,
            color: COLORS_MAP[this.formValues.color] ?? this.formValues.color,
            tableIds: [tableId]
          },
          headers: { "Content-Type": "application/json" }
        });

        this.formValues[tableId] = "";
        this.isNeedScroll[tableId] = true;
      } catch (error) {
        this.$toastr.e(error.response?.data?.message);
      } finally {
        this.isSending = false;
        this.loadChats(HISTORY_CHAT_HOURS);
      }
    },

    // Utility methods
    isValidMessage(message) {
      return message.match(/.*[^ ].*/g) !== null;
    },

    scrollDown() {
      setTimeout(() => {
        this.$refs.chatArea.scrollTop = this.$refs.chatArea.scrollHeight;
      }, 300);
    },

    validateState(ref) {
      const field = this.veeFields[ref];
      if (field && (field.dirty || field.validated)) {
        return !this.veeErrors.has(ref);
      }
      return null;
    },

    handleScroll(event) {
      const tableId = event.currentTarget.getAttribute("data-tableId");
      const currentScrollTop = event.currentTarget.scrollTop;
      const scrollHeight = event.currentTarget.scrollHeight - event.currentTarget.offsetHeight;

      this.isNeedScroll[tableId] = scrollHeight - currentScrollTop < 2;
    },

    // UI Helper methods
    getUserLinkClass(user) {
      return {
        'block-link': user.chatBlock === 1,
        'unblock-link': user.chatBlock === 0
      };
    },

    getBlockButtonClass(user) {
      return {
        'btn-primary': user.chatBlock === 0,
        'btn-danger': user.chatBlock === 1
      };
    },

    getMessageClass(message) {
      return {
        'message-out': message.type === 'admin',
        'message-in-system': message.type === 'system',
        'message-in-user': message.type === 'user'
      };
    },

    canBlockUser(user) {
      return user.userName !== 'admin' && user.generalOperatorId !== null;
    },

    navigateToChatHistory(user) {
      this.$router.push(`/chat-history/?playerId=${user.uid}`);
    },

    setSubmitType(type) {
      this.submitType = type;
    }
  }
};
</script>
<style>
.chat-area {
  background: #030303;
  height: 30vh;
  padding: 1em;
  overflow: auto;
  width: 650px;
  margin: 0 auto;
  box-shadow: 2px 2px 5px 2px rgba(0, 0, 0, 0.3);
}

.block-link {
  color: red;
}

.unblock-link {
  color: green;
}

.message {
  width: 99%;
  border-radius: 10px;
  padding: 0.5em;
  font-size: 1em;
}

.message-out {
  background: #2b2b2d;
  color: red;
}

.message-in-system {
  background: #2b2b2d;
  color: yellow;
}

.message-in-user {
  background: #2b2b2d;
  color: white;
}

.chat-inputs {
  display: flex;
  justify-content: space-between;
}

#person1-input {
  padding: 0.5em;
}

#person2-input {
  padding: 0.5em;
}
</style>